import './styles/global.scss';

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import { router } from './router';
import { vuetify } from './plugins/vuetify';
import { configureApi, setApiBase } from './api/client';
import { useAuthStore } from './stores/auth';

/**
 * Pier exposes a runtime config endpoint at `/_pier/env.json` containing
 * every env var that was set with `exposeToFrontend: true` (see
 * `scripts/set-pier-env.sh`). Fetching this lets us hot-swap the
 * backend URL without a frontend rebuild — same bundle can talk to
 * staging or prod just by flipping the Pier env var.
 *
 * Resilience: 2-second AbortSignal so we never block boot if
 * `/_pier/env.json` is slow / hung / 404 / down for cert reissue. On
 * local dev the file is missing entirely and we silently fall back to
 * the build-time `import.meta.env.VITE_API_BASE`.
 *
 * Timing: we await this BEFORE the very first API call (auth.fetchMe)
 * so that call uses the correct base. The 2s ceiling means worst-case
 * boot delay is bounded.
 */
async function loadRuntimeConfig(): Promise<void> {
  try {
    const res = await fetch('/_pier/env.json', {
      cache: 'no-store',
      signal: AbortSignal.timeout(2000),
    });
    if (!res.ok) return;
    const env = await res.json() as Record<string, string>;
    if (typeof env?.VITE_API_BASE === 'string' && env.VITE_API_BASE.length > 0) {
      setApiBase(env.VITE_API_BASE);
    }
  } catch (e) {
    console.warn('[bootstrap] /_pier/env.json unavailable; using build-time API base.', e);
  }
}

async function bootstrap() {
  // Bounded by AbortSignal.timeout(2000) inside — never blocks boot
  // longer than that, regardless of Pier-side hangs.
  await loadRuntimeConfig();

  const app = createApp(App);
  const pinia = createPinia();
  app.use(pinia);
  app.use(router);
  app.use(vuetify);

  const auth = useAuthStore();
  configureApi({
    getToken: () => auth.token,
    onUnauthorized: () => {
      auth.logout();
      if (router.currentRoute.value.name !== 'login') {
        router.replace({ name: 'login' });
      }
    },
  });

  if (auth.token) {
    try { await auth.fetchMe(); } catch { /* logout already handled */ }
  }

  app.mount('#app');

  // App-load push subscription resync. Per the iOS Web-Push article and
  // our own observed bug (DB-wipe / account-switch leaves the browser
  // with a working subscription that the SERVER doesn't know about):
  // every time the app opens, if the user is authenticated and notif
  // permission is already granted, idempotently re-POST the existing
  // browser subscription to /api/push/subscribe. subscribe() will reuse
  // the browser's existing PushSubscription object — no re-prompt, no
  // new endpoint — and upsert the row server-side. Safe to no-op away
  // from any failure: the explicit Profile flow is still the canonical
  // way for users to enable/test/disable.
  if (auth.token) {
    try {
      const { PUSH_SUPPORTED, getPushPermission, subscribe } = await import('@/api/push');
      if (PUSH_SUPPORTED && (await getPushPermission()) === 'granted') {
        await subscribe();
      }
    } catch { /* best-effort; Profile screen surfaces real failures */ }
  }

  // PWA auto-update: when a new service worker takes over (after Pier
  // deploy + close/reopen), reload once so the page picks up the new
  // bundle the SW now serves. The `controllerchange` event fires after
  // the new SW calls clients.claim() (configured in vite.config.ts).
  // The `__lockinReloaded` flag prevents an infinite refresh loop if
  // multiple controllerchange events fire in quick succession.
  if ('serviceWorker' in navigator) {
    let reloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  }
}

bootstrap();
